import { useState } from 'react';

import { useCurrentUser, useView } from '@/hooks';
import { AppMessageSeverities } from '@/types';
import { Dialog, DialogContent, Typography } from '@/ui';
import { toDocumentUrl, track } from '@/utils';

import Drop from './Drop';
import Footer from './Footer';

import type { FC } from 'react';

import { useUploadDocumentMutation } from '@/store/api/documentApi';
import { useAppDispatch } from '@/store/hooks';
import { setAppMessage } from '@/store/slices/appSlice';
import { captureException } from '@/utils/errorMonitoring';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const Modal: FC<ModalProps> = ({ isOpen, onClose }) => {
  const { user, isUserLoading } = useCurrentUser();
  const { isOverview, selectedUser, isViewLoading } = useView();
  const viewUser = isOverview ? selectedUser : user;

  const dispatch = useAppDispatch();
  const [uploadDocument, { isLoading: processing }] =
    useUploadDocumentMutation();

  const [preparedFiles, setPreparedFiles] = useState<File[]>([]);

  const onClick = async () => {
    try {
      const [file] = preparedFiles;
      if (!file) {
        return;
      }

      if (!viewUser.folderId) {
        dispatch(
          setAppMessage({
            message: 'Cannot upload files to user. Please contact support',
            open: true,
            severity: AppMessageSeverities.Error,
          }),
        );

        captureException(
          new Error(
            `Failed to upload document for user: ${viewUser.id} due to not having folderId`,
          ),
        );

        return;
      }

      const uploadResult = await uploadDocument({
        file,
        userId: viewUser.id,
      }).unwrap();
      const { mimeType, reference } = uploadResult;
      track('Uploaded File');

      const url = toDocumentUrl(mimeType, reference);
      window.open(url, '_blank');
      setPreparedFiles([]);
      onClose();
    } catch (e) {
      dispatch(
        setAppMessage({
          message: 'We’re sorry, there was an issue uploading the file',
          open: true,
          severity: AppMessageSeverities.Error,
        }),
      );
      captureException(e);
      onClose();
    }
  };

  // TODO: Loading state
  if (isUserLoading || isViewLoading) return null;

  return (
    <Dialog maxWidth="md" open={isOpen} fullWidth onClose={onClose}>
      <DialogContent className="min-h-50 p-12" dividers>
        <Typography variant="h4">Upload</Typography>
        <Drop
          preparedFile={preparedFiles[0] ?? ({} as File)}
          processing={processing}
          setPreparedFile={setPreparedFiles}
        />
      </DialogContent>
      <Footer
        noFiles={!preparedFiles.length}
        processing={processing}
        onClick={onClick}
        onClose={onClose}
      />
    </Dialog>
  );
};

export default Modal;
